import React, { useReducer, Suspense, useEffect } from 'react';
import { Box } from '@scope/scope-one-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import isEmpty from 'lodash/isEmpty';

import { ConfidentialAlert, Title, Loader } from 'ui';
import {
  SummaryCardLineChart,
  SummaryCardBusinessCard,
  HeadSquare,
  SummaryCard,
} from 'modules/common/SummaryCard';
import { SummaryCardCST } from 'modules/ClimateStressTest';
import { useGetAvailability } from 'modules/ClimateStressTest/services/climateStressTest';
import { SummaryCardRequestCreditReview } from 'modules/creditReview';
import { SummaryCardRequestCreditRating } from 'modules/creditRating';
import { SummaryCardRequestESG } from 'modules/ESG';
import { ScoreWidgetMainColumn } from 'ui/ScoreWidget';
import { generalInformation } from 'shared/constants/summaryTab';
import { creditRatingScalesMapping } from 'shared/mappings';
import { ratingConstants } from 'shared/constants/company';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { creditRatingStatuses } from 'shared/constants/creditRatingStatuses';
import { fetchCreditReviewSummaryTab, fetchCreditRatingSummaryTab } from 'services';
import { esgAvailable, fetchSentimentSummary, fetchSentimentAvailability } from 'services/esg';
import { useViewDocuments } from 'shared/hooks';
import {
  summaryCardMapCreditRatingParser,
  summaryCardHistoryParser,
  summaryCardDocumentsParser,
} from 'shared/parsers';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { getScoreWidgetFooterColor } from 'shared/functions';
import { getIndustryByName } from 'selectors/common';
import { getEntityShouldRefresh } from 'selectors/entity';
import { useStyles, useHeadSquareStyles } from 'shared/styles/summaryCard';
import {
  actions,
  setIsLoading,
  initialLoadingState,
  isLoadingReducer,
  setData,
  initialDataState,
  dataReducer,
  setStatus,
  initialStatusState,
  statusReducer,
  setSubscribed,
  initialSubscribedState,
  subscribedReducer,
} from 'shared/state/summaryCard';
import { moduleTypes } from 'shared/constants/moduleTypes';
import { shopProductsCategories } from 'modules/shop/shared/constants/shopProductsCategories';
import { useImpactReview } from 'modules/ESG/services/esgLendingService';
import { getSummary } from 'modules/ESG/parsers';
import { creditRatingAlerts } from 'shared/constants/creditRating';
import { errorCodes } from 'shared/constants/errorCodes';
import { useFeatures } from 'shared/hooks/useFeatures/useFeatures';
import { refreshEntityAction } from 'store/entity/entityActions';
import { parseSentimentData } from 'shared/parsers/esg/sentimentParser';
import Typography from '@mui/material/Typography';
import { SectionTitle } from './SectionTitle';
import { theme } from '../../../theme';
import { Instruments } from '../Tabs';

const SummaryCardTab = ({
  onChangeTabs,
  industry,
  countryCode,
  companyName,
  spv,
  creditRatingMethodologies,
  creditRatingDocuments,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { esgSentiment } = useFeatures();
  const entityShouldRefresh = useSelector(getEntityShouldRefresh);
  const headSquareClasses = useHeadSquareStyles();
  const { entityId } = useParams();
  const dispatch = useDispatch();
  const [subscribedState, subscribedDispatch] = useReducer(
    subscribedReducer,
    initialSubscribedState,
  );
  const [loadingState, loadingDispatch] = useReducer(isLoadingReducer, initialLoadingState);
  const [dataState, dataDispatch] = useReducer(dataReducer, initialDataState);
  const [statusState, statusDispatch] = useReducer(statusReducer, initialStatusState);
  const features = useFeatures();
  const industryObj = useSelector(getIndustryByName(industry));
  const { viewDocumentsModal, toggleModal, setDefaultExpand } = useViewDocuments({
    entityId,
    companyModule: moduleTypes.CREDIT_RATING,
    company: companyName,
    initialMethodologies: creditRatingMethodologies,
    initialDocuments: creditRatingDocuments,
  });
  const { data: isCSTAvailable } = useGetAvailability();
  const { orangeAccent } = theme.palette.custom;
  const refreshEntity = () => dispatch(refreshEntityAction());

  const { data: impactReview, loading: impactReviewLoading } = useImpactReview(entityId);

  const esgScore = getSummary(impactReview);

  const relatedSubscriptionData = {
    category: shopProductsCategories.creditAnalysis,
    productLine: 'Rating',
    countriesAndIndustries: [
      {
        country: countryCode,
        industries: industryObj ? [industryObj.name] : [],
      },
    ],
  };

  const fetchCreditRating = () => {
    loadingDispatch(setIsLoading(actions.setCreditRating, true));
    fetchCreditRatingSummaryTab(entityId)
      .then((data) => {
        dataDispatch(setData(actions.setCreditRating, data));
        loadingDispatch(setIsLoading(actions.setCreditRating, false));
        statusDispatch(setStatus(actions.setCreditRating, 'wasRequested', true));
        statusDispatch(setStatus(actions.setCreditRating, 'isAvailable', true));
        subscribedDispatch(setSubscribed(actions.setCreditRating, 'isSubscribed', true));
      })
      .catch(({ status, message, error }) => {
        const isSubscribed = Number(error) !== errorCodes.creditRating.isSubscribed;

        switch (status) {
          case creditRatingStatuses.notFound:
            subscribedDispatch(setSubscribed(actions.setCreditRating, 'isSubscribed', false));
            statusDispatch(setStatus(actions.setCreditRating, 'isAvailable', false));
            break;
          case creditRatingStatuses.forbidden:
            subscribedDispatch(
              setSubscribed(actions.setCreditRating, 'isSubscribed', isSubscribed),
            );
            statusDispatch(setStatus(actions.setCreditRating, 'isAvailable', true));
            statusDispatch(setStatus(actions.setCreditRating, 'wasRequested', false));
            break;
          default:
            throw new Error(message);
        }

        loadingDispatch(setIsLoading(actions.setCreditRating, false));
      });
  };

  const fetchCreditReview = () => {
    loadingDispatch(setIsLoading(actions.setCreditReview, true));
    fetchCreditReviewSummaryTab(entityId)
      .then((data) => {
        dataDispatch(setData(actions.setCreditReview, data));
        loadingDispatch(setIsLoading(actions.setCreditReview, false));
        statusDispatch(
          setStatus(actions.setCreditReview, 'wasRequested', data.wasRequested || false),
        );
        statusDispatch(setStatus(actions.setCreditReview, 'isAvailable', true));
      })
      .catch(() => {
        statusDispatch(setStatus(actions.setCreditReview, 'isAvailable', false));
        loadingDispatch(setIsLoading(actions.setCreditReview, false));
      });
  };

  const fetchESGSentiment = () => {
    loadingDispatch(setIsLoading(actions.setESGSentiment, true));

    fetchSentimentAvailability(entityId)
      .then(({ hasCompany, hasPermission, hasScore, requestState }) => {
        subscribedDispatch(setSubscribed(actions.setESGSentiment, 'isSubscribed', hasPermission));
        statusDispatch(setStatus(actions.setESGSentiment, 'isAvailable', hasCompany));
        statusDispatch(setStatus(actions.setESGSentiment, 'requestState', requestState));
        statusDispatch(setStatus(actions.setESGSentiment, 'dataExists', hasScore));

        const hasSentifyScore = hasCompany && hasPermission && hasScore;

        return (
          hasSentifyScore &&
          fetchSentimentSummary(entityId).then((data) => {
            dataDispatch(setData(actions.setESGSentiment, parseSentimentData(data)));
          })
        );
      })
      .catch(() => {
        subscribedDispatch(setSubscribed(actions.setESGSentiment, 'isSubscribed', false));
        statusDispatch(setStatus(actions.setESGSentiment, 'isAvailable', false));
        statusDispatch(setStatus(actions.setESGSentiment, 'requestState', undefined));
        statusDispatch(setStatus(actions.setESGSentiment, 'dataExists', false));
      })
      .finally(() => {
        loadingDispatch(setIsLoading(actions.setESGSentiment, false));
      });
  };

  const fetchESGAvailability = () => {
    esgAvailable(entityId)
      .then(({ subscribed, requestState }) => {
        subscribedDispatch(setSubscribed(actions.setESGScore, 'isSubscribed', subscribed));
        statusDispatch(setStatus(actions.setESGScore, 'isSubscribed', subscribed));
        statusDispatch(setStatus(actions.setESGScore, 'requestState', requestState));
      })
      .catch(() => {
        subscribedDispatch(setSubscribed(actions.setESGScore, 'isSubscribed', false));
        statusDispatch(setStatus(actions.setESGScore, 'isSubscribed', false));
        statusDispatch(setStatus(actions.setESGScore, 'requestState', undefined));
      });
  };

  const handleEsgRequested = (requested) => {
    if (requested) refreshEntity();
  };

  const handleEsgUnlocked = (unlocked) => {
    if (unlocked) refreshEntity();
  };

  useEffect(() => {
    if (features.creditReview) {
      fetchCreditReview();
    }

    if (features.esgSentiment) {
      fetchESGSentiment();
    }

    fetchCreditRating();
    fetchESGAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityShouldRefresh]);

  const statusIncludesNegativeValue = (type) => Object.values(statusState[type]).includes(false);
  const subscribedIncludesNegativeValue = (type) =>
    Object.values(subscribedState[type]).includes(false);

  const creditRatingData =
    !loadingState.creditRating &&
    dataState.creditRating.data &&
    !dataState.creditRating.data.some((item) => !item.latestRating)
      ? summaryCardMapCreditRatingParser(dataState.creditRating.data)[0]
      : {};
  const showCreditRatingSummaryCard =
    loadingState.creditRating ||
    (!isEmpty(creditRatingData) &&
      !statusIncludesNegativeValue('creditRating') &&
      !subscribedIncludesNegativeValue('creditRating'));
  const showCreditReviewSummaryCard =
    loadingState.creditReview || !statusIncludesNegativeValue('creditReview');
  const showESGScoreSummaryCard =
    impactReviewLoading || (!!esgScore && !subscribedIncludesNegativeValue('esgScore'));
  const showESGSentimentSummaryCard =
    loadingState.esgSentiment ||
    (!statusIncludesNegativeValue('esgSentiment') &&
      !subscribedIncludesNegativeValue('esgSentiment'));

  const showConfidentialLabel =
    !loadingState.creditRating && !creditRatingData.isPublic && !!creditRatingData.score;

  if (spv) {
    return (
      <>
        <Suspense fallback={<Loader center />}>
          {showConfidentialLabel && (
            <Box display="flex" flexDirection="column" mb={2}>
              <ConfidentialAlert
                label={t(creditRatingAlerts.confidentialAlert.title)}
                message={t(creditRatingAlerts.confidentialAlert.message)}
              />
            </Box>
          )}
          <Paper variant="elevation" elevation={3}>
            <Instruments />
          </Paper>
        </Suspense>
      </>
    );
  }

  return (
    <>
      {showConfidentialLabel && (
        <Box display="flex" flexDirection="column" mb={2}>
          <ConfidentialAlert
            label={t(creditRatingAlerts.confidentialAlert.title)}
            message={t(creditRatingAlerts.confidentialAlert.message)}
          />
        </Box>
      )}
      <SectionTitle
        label={t('Credit Rating')}
        ratingType={ratingTypes.CREDIT_RATING}
        onChangeTabs={onChangeTabs}
      />
      {showCreditRatingSummaryCard ? (
        <SummaryCard
          headSquare={
            !loadingState.creditRating && (
              <ScoreWidgetMainColumn
                className={headSquareClasses.scoreWidgetMainColumn}
                value={creditRatingData.score || t('N/A')}
                label={creditRatingData.scoreTitle}
                footer={`${t('Outlook')}: ${creditRatingData.outlook || t('N/A')}`}
                footerColor={getScoreWidgetFooterColor(creditRatingData.outlook)}
                accent={creditRatingData.generalInformationList.includes(
                  'Unsolicited' && 'Without participation ',
                )}
              />
            )
          }
          main={
            <Grid container className={classes.businessCards} justifyContent="space-around">
              {!loadingState.creditRating &&
                creditRatingData.businessCards.map((businessCard) => (
                  <Grid item key={businessCard.name}>
                    <Title className={classes.titleMarginBottom}>{t(businessCard.title)}</Title>
                    <SummaryCardBusinessCard
                      id={businessCard.id}
                      name={businessCard.name}
                      position={businessCard.position}
                      industry={businessCard.industry}
                    />
                  </Grid>
                ))}
            </Grid>
          }
          history={
            <SummaryCardLineChart
              ratingScale={creditRatingData.ratingScale}
              historyType={portfolioTypes.CREDIT_RATING}
              history={creditRatingData.history}
              latestChange={creditRatingData.latestChange}
            />
          }
          className={classes.summaryCard}
          isLoading={loadingState.creditRating}
          data={creditRatingData}
          documentHandling={{
            toggleModal,
            defaultDocument: setDefaultExpand,
          }}
        />
      ) : (
        <div className={classes.summaryCard}>
          <SummaryCardRequestCreditRating
            entityId={entityId}
            isAvailable={statusState.creditRating.isAvailable}
            subscribed={subscribedState.creditRating.isSubscribed}
            data={relatedSubscriptionData}
            onUnlock={refreshEntity}
          />
        </div>
      )}
      <SectionTitle
        label={t('Credit Review')}
        ratingType={ratingTypes.CREDIT_REVIEW}
        onChangeTabs={onChangeTabs}
      />
      {features.creditReview &&
        (showCreditReviewSummaryCard ? (
          <SummaryCard
            headSquare={
              <HeadSquare title={t('Short term')}>
                <Typography
                  color={
                    generalInformation.includes('Unsolicited' && 'Without entity participation')
                      ? orangeAccent
                      : ''
                  }
                >
                  <span className={headSquareClasses.score}>{dataState.creditReview.score}</span>
                  <span className={headSquareClasses.max}>/10</span>
                </Typography>
              </HeadSquare>
            }
            history={
              <SummaryCardLineChart
                history={summaryCardHistoryParser(dataState.creditReview.history)}
              />
            }
            className={classes.summaryCard}
            isLoading={loadingState.creditReview}
            data={{
              generalInformationList: generalInformation,
              ratingScalesData: creditRatingScalesMapping(
                portfolioTypes.CREDIT_REVIEW,
                ratingConstants.entityReview,
              ),
              ...dataState.creditReview,
            }}
          />
        ) : (
          <div className={classes.summaryCard}>
            <SummaryCardRequestCreditReview
              easyNumber={dataState.creditReview.easyNumber}
              onRequest={fetchCreditReview}
              {...statusState.creditReview}
            />
          </div>
        ))}
      {!features.creditReview && (
        <div className={classes.summaryCard}>
          <SummaryCardRequestCreditReview isAvailable={false} />
        </div>
      )}
      <SectionTitle
        label={t('ESG Impact')}
        ratingType={ratingTypes.ESG_IMPACT}
        onChangeTabs={onChangeTabs}
      />
      {showESGScoreSummaryCard ? (
        <SummaryCard
          headSquare={
            <HeadSquare title={t('Impact review')}>
              <Typography
                color={
                  generalInformation.includes('Unsolicited' && 'Without entity participation')
                    ? orangeAccent
                    : ''
                }
              >
                <span className={headSquareClasses.score}>{esgScore?.score}</span>
                <span className={headSquareClasses.max}>/10</span>
              </Typography>
            </HeadSquare>
          }
          history={
            <SummaryCardLineChart
              history={summaryCardHistoryParser(esgScore?.history)}
              isESGImpact
            />
          }
          className={classes.summaryCard}
          isLoading={impactReviewLoading}
          dataQuality={impactReview?.dataQuality}
          data={{
            ...esgScore,
            documents: summaryCardDocumentsParser(esgScore?.documents),
            generalInformationList: generalInformation,
            ratingScalesData: creditRatingScalesMapping(
              portfolioTypes.ESG_ANALYSIS,
              ratingConstants.entityReview,
            ),
          }}
        />
      ) : (
        <div className={classes.summaryCard}>
          <SummaryCardRequestESG
            scopeoneId={entityId}
            dataExists={!!esgScore}
            isSubscribed={subscribedState.esgScore.isSubscribed}
            isAvailable={!!esgScore}
            industry={industry}
            countryCode={countryCode}
            onRequested={handleEsgRequested}
            onUnlocked={handleEsgUnlocked}
          />
        </div>
      )}
      {esgSentiment && (
        <>
          <SectionTitle
            label={t('ESG Sentiment')}
            ratingType="esg-sentiment"
            onChangeTabs={onChangeTabs}
          />
          {showESGSentimentSummaryCard ? (
            <SummaryCard
              onlyHeader
              headSquare={
                <HeadSquare title={t('Sentiment review')}>
                  <span className={headSquareClasses.score}>{dataState.esgSentiment.score}</span>
                  <span className={headSquareClasses.max}>/10</span>
                </HeadSquare>
              }
              history={
                <SummaryCardLineChart
                  history={summaryCardHistoryParser(dataState.esgSentiment.history)}
                  isESGImpact
                  isESGSentiment
                />
              }
              className={classes.summaryCard}
              isLoading={loadingState.esgSentiment}
              data={{
                ...dataState.esgSentiment,
                documents: summaryCardDocumentsParser(dataState.esgSentiment.documents),
                generalInformationList: generalInformation,
                ratingScalesData: creditRatingScalesMapping(
                  portfolioTypes.ESG_ANALYSIS,
                  ratingConstants.entityReview,
                ),
              }}
            />
          ) : (
            <div className={classes.summaryCard}>
              <SummaryCardRequestESG
                type="sentiment"
                scopeoneId={entityId}
                dataExists={statusState.esgSentiment.dataExists}
                isSubscribed={subscribedState.esgSentiment.isSubscribed}
                isAvailable={statusState.esgSentiment.isAvailable}
                requestState={statusState.esgSentiment.requestState}
                industry={industry}
                countryCode={countryCode}
                onRequested={handleEsgRequested}
                onUnlocked={handleEsgUnlocked}
              />
            </div>
          )}
        </>
      )}
      {!isCSTAvailable && (
        <>
          <SectionTitle
            label={t('Climate Stress Test')}
            ratingType="climate-stress-test"
            onChangeTabs={onChangeTabs}
          />
          <div className={classes.summaryCard}>
            <SummaryCardCST onUnlocked={handleEsgUnlocked} />
          </div>
        </>
      )}
      {viewDocumentsModal}
    </>
  );
};

SummaryCardTab.propTypes = {
  onChangeTabs: PropTypes.func.isRequired,
  industry: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  creditRatingMethodologies: PropTypes.array.isRequired,
  creditRatingDocuments: PropTypes.array.isRequired,
};

export { SummaryCardTab };
